import type { PdfJobStatusResponse } from '../../types/pdfImport';
import type { ResponseType } from '../../types/responses';

const codeMessages: Record<string, string> = {
  pdf_import_disabled: 'PDF import is not available yet.',
  pdf_processor_unavailable: 'PDF analysis is temporarily unavailable. Please try again later.',
  pdf_upload_not_found: 'The upload was not found. Please choose the PDF again.',
  pdf_upload_expired: 'The upload expired. Please choose the PDF again.',
  pdf_upload_active_conflict: 'An upload is already in progress. Please check its status.',
  pdf_job_active_conflict: 'A PDF analysis is already running. Its current status is shown below.',
  pdf_idempotency_conflict: 'This upload has already been used with another request.',
  pdf_upload_used: 'This upload has already been used. Please choose the PDF again.',
  pdf_verification_in_progress: 'The upload is still being verified. Please check its status.',
  pdf_source_binding_conflict: 'The uploaded file changed. Please choose the PDF again.',
  pdf_file_missing: 'The uploaded PDF could not be found. Please choose it again.',
  pdf_file_empty: 'The PDF is empty. Please choose another file.',
  pdf_file_too_large: 'The PDF must be 25 MB or smaller.',
  pdf_invalid_content_type: 'Please choose a PDF file.',
  pdf_invalid_format: 'The PDF is invalid or damaged. Please choose another file.',
  pdf_stream_incomplete: 'The PDF upload was incomplete. Please choose it again.',
  pdf_verification_timeout:
    'PDF verification timed out. Please check the current job before trying again.',
  pdf_storage_unavailable: 'PDF storage is temporarily unavailable.',
  pdf_storage_not_versioned: 'PDF storage is not ready for imports.',
  pdf_database_unavailable: 'PDF import is temporarily unavailable.',
  pdf_cancellation_conflict: 'The analysis has already finished. Its status has been refreshed.',
  pdf_preview_expired: 'The previous PDF preview has expired.',
  pdf_preview_superseded: 'The previous PDF preview has been replaced.',
  pdf_preview_not_ready: 'The PDF preview is not ready yet.',
  invalid_source: 'The PDF is invalid or damaged.',
  processing_failed: 'PDF analysis failed. Please try another file.',
  transient_provider_error: 'PDF analysis failed because the processing service was unavailable.',
  provider_timeout: 'PDF analysis timed out.',
  rate_limited: 'PDF analysis could not finish because the processing service is busy.',
  lease_expired: 'PDF analysis could not finish. Please try again.',
  attempt_limit_reached: 'PDF analysis could not finish after several attempts.',
  document_budget_exhausted: 'The PDF is too complex to process.',
  pdf_page_limit_exceeded: 'The PDF must contain 20 pages or fewer.',
  pdf_too_large: 'The PDF must be 25 MB or smaller.',
};

export const pdfImportErrorMessage = (
  response: Pick<ResponseType<unknown>, 'status' | 'code'>,
  fallback = 'PDF import is temporarily unavailable. Please try again.'
): string => {
  if (response.status === 401) return 'Please sign in to import a PDF.';
  if (response.status === 403) return 'Your account or tariff does not allow PDF import.';
  if (response.code && codeMessages[response.code]) return codeMessages[response.code];
  if (response.status === 404) return 'PDF import is not available yet.';
  if (response.status === 413) return codeMessages.pdf_file_too_large;
  if (response.status === 415) return codeMessages.pdf_invalid_content_type;
  if (response.status === 409)
    return 'The PDF import conflicts with an existing job. Please check its status.';
  if (response.status === 400 || response.status === 422)
    return 'The PDF could not be accepted. Please check the file and try again.';
  return fallback;
};

export const pdfJobErrorMessage = (job: PdfJobStatusResponse): string =>
  (job.errorCode && codeMessages[job.errorCode]) || 'PDF analysis failed. Please try again.';
